'use client';

import React from 'react';
import { useSelector } from 'react-redux';

import Image from 'next/image';
import Link from 'next/link';

import Icon2 from '@/components/elements/icon/icon2';
import ExpanderContacts from '@/components/header/expander-contacts';
import ExpanderLanguage from '@/components/header/expander-language';
import { useTranslation } from '@/i18n/client';
import { selectIsLoggedIn, selectUserName } from '@/store/auth';
import { assetsShared, externalLinks } from '@/utils/constants';
import { buildUrl } from '@/utils/navigation';
import { createHeaderTestId } from '@/utils/string-utils';
import { getScrollRem } from '@/utils/window-utils';

import { HeaderPopupKeys, HeaderProps } from './header.types';

export default function HeaderMobile(props: HeaderProps) {
    const { t: tCo } = useTranslation(props.lang, 'common');
    const { t: tAu } = useTranslation(props.lang, 'auth');

    const isLoggedIn = useSelector(selectIsLoggedIn);
    const userName = useSelector(selectUserName);

    const countrySlug = props.country?.slug || 'serbia';

    const closeAll = () => {
        props.onClose(HeaderPopupKeys.auth);
        props.onClose(HeaderPopupKeys.navbar);
    };

    const closeNavBar = () => {
        props.onClose(HeaderPopupKeys.navbar);
    };

    return (
        <>
            {/* nav-bar when the menu is closed */}
            <div className="flex flex-nowrap items-center gap-2 xl:hidden">
                {isLoggedIn && userName ? (
                    <div className="flex h-6 w-6 justify-center rounded-full bg-accent">
                        <span className="m-auto block capitalize text-white">{userName.slice(0, 1) || ' '}</span>
                    </div>
                ) : null}

                {props.isVisible.auth ? (
                    <button
                        className="flex h-8 w-8 items-center justify-center"
                        onClick={closeAll}
                        aria-label={tCo('common.close')}
                        data-testid={createHeaderTestId({ name: 'close-menu-btn' })}
                    >
                        <Icon2 name="phi-x-16" iconClass="h-5 w-5" />
                    </button>
                ) : (
                    <button
                        className="flex h-8 w-8 items-center justify-center"
                        onClick={() => props.onOpen(HeaderPopupKeys.navbar)}
                        aria-label={tCo('navigation.menu')}
                        data-testid={createHeaderTestId({ name: 'menu-btn' })}
                    >
                        <Icon2 name="phi-list-18" iconClass="h-5 w-5" />
                    </button>
                )}
            </div>

            {/* open menu */}
            {props.isVisible[HeaderPopupKeys.navbar] && (
                <div className="fixed bottom-0 left-0 right-0 top-0 z-[3] h-full bg-white xl:hidden">
                    {/* nav-bar when the menu is open (inside the menu) */}
                    <div
                        className={`flex h-[3.75rem] items-center justify-between border-b border-solid border-gray-300 bg-white px-4 lg:px-10 ${getScrollRem() >= 2 ? 'h-[3.75rem]' : 'h-[5.75rem]'}`}
                    >
                        <Link
                            href={buildUrl('', { children: [countrySlug] })}
                            scroll={false}
                            style={{ width: `169.5px`, height: `46px` }}
                        >
                            <Image width={169.5} height={46} src={assetsShared.logoPrimary} alt="logo" />
                        </Link>
                        <button
                            className="flex h-8 w-8 items-center justify-center"
                            onClick={closeAll}
                            aria-label={tCo('common.close')}
                            data-testid={createHeaderTestId({ name: 'close-menu-btn' })}
                        >
                            <Icon2 name="phi-x-16" iconClass="h-5 w-5" />
                        </button>
                    </div>

                    {/* menu content */}
                    <div className="flex flex-col gap-0">
                        {isLoggedIn ? (
                            <Link
                                href={buildUrl('user', { lang: props.lang })}
                                className="md:text-regular flex w-full items-center gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                data-testid={createHeaderTestId({ name: 'user-cabinet-link' })}
                            >
                                {userName && (
                                    <div className="h-6 w-6 rounded-full bg-accent text-center">
                                        <span className="text m-auto block text-base capitalize text-white">
                                            {userName.slice(0, 1) || ''}
                                        </span>
                                    </div>
                                )}
                                <span>{tAu('buttons.profile')}</span>
                            </Link>
                        ) : (
                            <button
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={() => props.onOpen(HeaderPopupKeys.auth)}
                                data-testid={createHeaderTestId({ name: 'auth-btn' })}
                            >
                                {/* todo: profile icon */}
                                {tAu('buttons.loginShort')}
                            </button>
                        )}

                        <ExpanderLanguage lang={props.lang} />
                        <ExpanderContacts country={props.country} lang={props.lang} />

                        <nav className="flex flex-col gap-0 border-t border-solid border-gray-300">
                            <Link
                                href={buildUrl('', {
                                    lang: props.lang,
                                    children: [countrySlug],
                                    fragment: props.isCalendar ? 'events' : undefined,
                                })}
                                scroll={!props.isCalendar}
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={closeNavBar}
                                data-testid={createHeaderTestId({ name: 'main-link' })}
                            >
                                {tCo('events')}
                            </Link>
                            <Link
                                href={buildUrl('', { lang: props.lang, children: [countrySlug, 'services'] })}
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={closeNavBar}
                                data-testid={createHeaderTestId({ name: 'services-link' })}
                            >
                                {tCo('services')}
                            </Link>
                            <Link
                                href={buildUrl('about', { lang: props.lang })}
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={closeNavBar}
                                data-testid={createHeaderTestId({ name: 'about-link' })}
                            >
                                {tCo('about')}
                            </Link>
                            <Link
                                href={buildUrl('collaboration', { lang: props.lang })}
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={closeNavBar}
                                data-testid={createHeaderTestId({ name: 'collaboration-link' })}
                            >
                                {tCo('collaboration')}
                            </Link>
                            <Link
                                href={`${externalLinks.tickets[props.lang]}`}
                                className="md:text-regular flex w-full gap-2 whitespace-nowrap px-4 py-4 text-sm"
                                onClick={closeNavBar}
                            >
                                {tCo('tickets')}
                            </Link>
                        </nav>
                    </div>
                </div>
            )}
        </>
    );
}
